// Library methods
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import { DateTime } from "luxon";

// Utilities
import { addExam as examAdd } from "../../services/Exam";
import { examCardEnums } from "../../enums";
import useClinic from "../useClinic";
import { ToastContext } from "../../contexts/ToastContext";

export const useDuplicateExams = () => {
  const { clinicId, clinicTimezone } = useClinic();
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const { setToast } = useContext(ToastContext);

  const duplicateExamData = useCallback(
    async ({ sectionTypeOrder, examSections, patientId }) => {
      const today = DateTime.now()
        .setZone(clinicTimezone)
        .startOf("day")
        .toUTC()
        .toISO();

      const examData = {
        sectionTypeOrder: sectionTypeOrder.join(", "),
        scheduledDate: today,
        ...examSections,
      };

      // add exam to exams api
      let newExam = null;

      try {
        const token = await getAccessTokenSilently();
        newExam = await examAdd(token, clinicId, patientId, examData);
      } catch (error) {
        setToast(() => ({
          success: false,
          message: t("exams_add_error"),
        }));
      } finally {
        return newExam;
      }
    },
    [clinicId, clinicTimezone, setToast, getAccessTokenSilently, t]
  );

  const getFormattedExamSections = useCallback(({ selectedDuplicateExam }) => {
    let examSections = {};

    const sectionNames = Object.values(examCardEnums).map(
      (value) => value.sectionName
    );
    sectionNames.forEach((sectionName) => {
      if (
        selectedDuplicateExam?.[sectionName] &&
        selectedDuplicateExam?.[sectionName]?.length > 0
      ) {
        examSections[sectionName] = [...selectedDuplicateExam[sectionName]];
      }
    });

    return examSections;
  }, []);

  const duplicateExam = useCallback(
    async ({ selectedDuplicateExam, patientId, onDuplicateExamCallback }) => {
      const examSections = getFormattedExamSections({ selectedDuplicateExam });
      const newExam = await duplicateExamData({
        sectionTypeOrder: selectedDuplicateExam.sectionTypeOrder,
        examSections,
        patientId,
      });
      await onDuplicateExamCallback(newExam);
    },
    [getFormattedExamSections, duplicateExamData]
  );

  return { duplicateExam };
};
